/** @jsx isolateComponent */

import { css } from '@emotion/core';
import {
  Animation,
  Colors,
  Icon,
  Link,
  Typography,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
} from '@zapier/common-utils';
import type { UniversalNavLinkType } from '@zapier/universal-nav/dist/constants';

export type Props = {
  /**
   * Array of links to render as a submenu.
   */
  links: Array<UniversalNavLinkType>;
  /**
   * The toggle text of the submenu.
   */
  text: string;
};

const Styles = labelStyles(__filename, {
  summary: () => [
    css`
      display: block;
      border-radius: 10px;
      color: ${Colors.grey700};

      &:hover, &:focus {
        color: ${Colors.black};
        background-color: ${Colors.grey100};
      }

      [open] > & {
        color: ${Colors.blue500};
        background-color: ${Colors.blue100};
      }
    `,
    css`
      /* Hides default icon on details element */
      &::-webkit-details-marker {
        display: none;
      }
      /* Hide default icon from IE. */
      &::before {
        display: none;
      }
    `,
  ],

  // Wrapper so layout works in Safari.
  summaryInner: css`
    ${Typography.base};
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
    min-height: 50px;
    padding: 10px 15px;
    white-space: nowrap;
    width: 100%;
    transition: ${Animation.transitionValue};
  `,

  caret: css`
    transition: ${Animation.transitionValue};

    [open] & {
      transform: rotate(-180deg);
    }
  `,

  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
    padding: 10px;
    border-bottom: 1px solid ${Colors.grey200};
  `,

  link: css`
    ${Typography.base};
    display: block;
    border-radius: 10px;
    color: ${Colors.grey700};
    line-height: 20px;
    font-size: 14px;
    padding: 10px 15px;

    &:hover, &:focus {
      color: ${Colors.black};
      background-color: ${Colors.grey100};
    }
  `,

  linkInner: css`
    transform: translateY(1px);
  `,

  linkTitle: css`
    display: block;
    font-weight: 600;
  `,

  linkDescription: css`
    display: block;
    color: ${Colors.grey700};
    margin-top: 5px;
    font-size: 12px;
    line-height: 15px;
  `,
});

const SubmenuLink = (props: UniversalNavLinkType) => {
  return (
    <Link color={null} css={Styles.link} href={props.href}>
      <div css={Styles.linkInner}>
        <p css={Styles.linkTitle}>{props.text}</p>
        {props.description && (
          <p css={Styles.linkDescription}>
            {props.description}
          </p>
        )}
      </div>
    </Link>
  );
};

/**
 * Renders a submenu within the universal sidebar.
 */
const UniversalSidebarSubmenu = (props: Props) => {
  const linkItems = props.links.map((link: UniversalNavLinkType) => {
    return (
      <li key={link.text}>
        <SubmenuLink {...link} />
      </li>
    );
  });
  return (
    <details>
      <summary css={Styles.summary()}>
        <div css={Styles.summaryInner}>
          <span>
            {props.text}
          </span>
          <span css={Styles.caret}>
            <Icon isBlock={true} name="arrowSmallDown" size={24} />
          </span>
        </div>
      </summary>
      <ul css={Styles.list}>
        {linkItems}
      </ul>
    </details>
  );
};

export default UniversalSidebarSubmenu;
